import {
	Popover,
	PopoverContent,
	PopoverTrigger
} from "~/components/ui/popover"
import { DetailsSection } from "~/components/details/DetailsSection"
import { Button } from "~/components/ui/button"
import { Item } from "~/components/ui/item"
import { GitPullRequestIcon, LinkIcon } from "lucide-react"
import { useMemo } from "react"
import { ActivityExternalLink } from "~/components/ActivityExternalLink"
import { api, type RouterOutputs } from "~/trpc/react"

export function IssueGitHubPanel({
	compact = false,
	issueId,
	teamId,
	githubRepo,
	githubIssueNumber,
	pullRequests
}: {
	compact?: boolean
	issueId: string
	teamId: string
	githubRepo: { fullName: string } | null
	githubIssueNumber: number | null
	pullRequests: NonNullable<RouterOutputs["issues"]["get"]>["pullRequests"]
}) {
	const pullRequestsQuery = api.github.issuePullRequests.useQuery(
		{
			issueId,
			teamId
		},
		{
			enabled: pullRequests.length > 0
		}
	)
	const displayedPullRequests = useMemo(
		() =>
			pullRequestsQuery.data ??
			pullRequests.map((pr) => ({
				...pr,
				title: `Pull request #${pr.githubPrNumber}`,
				htmlUrl: `https://github.com/${pr.githubRepo.fullName}/pull/${pr.githubPrNumber}`,
				branchName: "",
				isDraft: false,
				isMerged: false,
				isClosed: false
			})),
		[pullRequestsQuery.data, pullRequests]
	)

	const content = (
		<DetailsSection title="GitHub" className="min-w-0 overflow-hidden">
			<div className="flex min-w-0 flex-col gap-3">
				<div className="flex min-w-0 items-center justify-between gap-3">
					<span className="text-sm font-medium text-text-sub">Repo</span>
					<span className="flex min-w-0 items-center gap-2 overflow-hidden text-sm text-text-sub">
						<GitPullRequestIcon className="size-4 shrink-0 text-icons" />
						<span className="truncate">
							{githubRepo?.fullName ?? "No repo"}
						</span>
					</span>
				</div>
				{githubRepo && githubIssueNumber && (
					<Button asChild variant="outline" className="justify-start">
						<ActivityExternalLink
							href={`https://github.com/${githubRepo.fullName}/issues/${githubIssueNumber}`}
							className="min-w-0 overflow-hidden"
						>
							<LinkIcon className="size-4" />
							<span className="truncate">
								{githubRepo.fullName}#{githubIssueNumber}
							</span>
						</ActivityExternalLink>
					</Button>
				)}
				<div className="flex min-w-0 flex-col gap-2">
					<p className="text-sm font-medium text-text-sub">Pull requests</p>
					{pullRequestsQuery.isError || pullRequestsQuery.isRefetchError ? (
						<div className="rounded-md control-raised control-pressable bg-background-elevated px-3 py-2 text-sm text-text-sub">
							Could not load pull request details from GitHub.
						</div>
					) : displayedPullRequests.length === 0 ? (
						<p className="text-sm text-text-sub">No linked PRs</p>
					) : (
						displayedPullRequests.map((pr) => (
							<Item asChild variant="raised" size="compact" key={pr.id}>
								<ActivityExternalLink
									href={pr.htmlUrl}
									className="min-w-0 flex-col items-start overflow-hidden"
								>
									<span className="block min-w-0 truncate font-medium text-text-main">
										#{pr.githubPrNumber} {pr.title}
									</span>
									<span className="flex min-w-0 items-center gap-1 text-xs text-text-sub">
										<span className="shrink-0">
											{pr.isMerged
												? "Merged"
												: pr.isClosed
													? "Closed"
													: pr.isDraft
														? "Draft"
														: "Open"}
										</span>
										{pr.branchName ? (
											<>
												<span className="shrink-0">·</span>
												<span className="min-w-0 truncate">
													{pr.branchName}
												</span>
											</>
										) : null}
									</span>
								</ActivityExternalLink>
							</Item>
						))
					)}
				</div>
			</div>
		</DetailsSection>
	)
	if (!compact) return content
	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					variant="picker-outline"
					size="sm"
					shape="pill"
					aria-label="GitHubPanel"
				>
					<GitPullRequestIcon className="size-4" />
					<span className="max-w-48 truncate">
						{githubRepo?.fullName ?? "No repo"}
					</span>
				</Button>
			</PopoverTrigger>
			<PopoverContent size="lg" layout="panel">
				{content}
			</PopoverContent>
		</Popover>
	)
}
